// Modal reutilizable del reporte de campo del boomero.
// Usado en BoomeroPage (lista activa) y en CedulasAplicacionPage (historial,
// para que el ingeniero pueda editar/corregir un reporte ya finalizado).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabase';
import type { AppBoleta, AppBoletaCampo, AppBoom } from '../../types/empacadora';

interface GpsPos {
  lat: number;
  lng: number;
  accuracy: number;
  at: string;
}

function obtenerUbicacion(timeoutMs = 15000): Promise<GpsPos> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Este dispositivo no soporta GPS.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        at: new Date().toISOString(),
      }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) reject(new Error('Permiso de ubicación denegado. Activá la ubicación del dispositivo.'));
        else if (err.code === err.POSITION_UNAVAILABLE) reject(new Error('No se pudo obtener la ubicación (sin señal GPS).'));
        else if (err.code === err.TIMEOUT) reject(new Error('Tiempo agotado al obtener la ubicación. Intentá de nuevo.'));
        else reject(new Error(err.message || 'Error al obtener ubicación.'));
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

function fmtCoord(lat?: number | null, lng?: number | null) {
  if (lat == null || lng == null) return '—';
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const MARCHAS = ['1ra', '2da', '3ra', '4ta', 'N/A'];

const CAMPO_VACIO = (empresaId: number, boletaId: number, boomeroNombre: string): AppBoletaCampo => ({
  empresa_id: empresaId,
  boleta_id: boletaId,
  boomero_nombre: boomeroNombre,
  boom_descripcion: '',
  rpm: undefined,
  presion_bar: undefined,
  marcha: '1ra',
  caldo_sobrante: false,
  caldo_sobrante_litros: undefined,
  clima_nublado: false,
  clima_lluvioso: false,
  clima_soleado: false,
  clima_ventoso: false,
  clima_no_apto: false,
  metodo_aspersion: false,
  metodo_inmersion: false,
  metodo_nebulizacion: false,
  cultivo: false,
  semillero: false,
  area_barbecho: false,
  observaciones: '',
  registrado_por: boomeroNombre,
});

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        fontSize: '11px', fontWeight: 700, color: 'var(--ink-muted)',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px',
      }}>{titulo}</div>
      {children}
    </div>
  );
}

function CheckChip({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)} style={{
      padding: '10px 16px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
      cursor: 'pointer', border: `1.5px solid ${checked ? '#22c55e' : 'rgba(255,255,255,0.12)'}`,
      background: checked ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
      color: checked ? '#4ade80' : 'var(--ink-muted)', transition: 'all 0.15s',
    }}>
      {checked ? '✓ ' : ''}{label}
    </button>
  );
}

function NumInput({ label, value, onChange, placeholder = '0.00' }: {
  label: string; value?: number; onChange: (v?: number) => void; placeholder?: string;
}) {
  const [raw, setRaw] = useState(value != null ? String(value) : '');
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '4px' }}>{label}</div>
      <input
        type="text" inputMode="decimal"
        value={raw}
        placeholder={placeholder}
        onFocus={e => e.target.select()}
        onChange={e => {
          const v = e.target.value.replace(/[^0-9.,]/g, '');
          setRaw(v);
          const n = parseFloat(v.replace(',', '.'));
          onChange(isNaN(n) ? undefined : n);
        }}
        onBlur={e => {
          const n = parseFloat(e.target.value.replace(',', '.'));
          if (!isNaN(n)) {
            setRaw(n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
            onChange(n);
          }
        }}
        style={{
          width: '100%', padding: '12px 14px', borderRadius: '10px', fontSize: '20px',
          fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
          border: '1.5px solid var(--line)', background: 'var(--surface-base)',
          color: '#f8fafc', outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

export interface ModalReporteCampoProps {
  boleta: AppBoleta;
  empresaId: number;
  usuario: string;
  booms: AppBoom[];
  onClose: () => void;
  onGuardado?: () => void; // callback opcional al guardar exitosamente
}

export function ModalReporteCampo({ boleta, empresaId, usuario, booms, onClose, onGuardado }: ModalReporteCampoProps) {
  const [campo, setCampoState] = useState<AppBoletaCampo>(CAMPO_VACIO(empresaId, boleta.id, usuario));
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [capturandoGps, setCapturandoGps] = useState<'inicio' | 'fin' | null>(null);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null);
  const [autoSave, setAutoSave] = useState<'idle' | 'pendiente' | 'guardando' | 'guardado' | 'error'>('idle');
  const autoSaveTimerRef = useRef<number | null>(null);
  const ultimaCampoSerialRef = useRef<string>('');

  function showMsg(tipo: 'ok' | 'err', texto: string) {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg(null), 3500);
  }

  const setCampo = useCallback((fn: (prev: AppBoletaCampo) => AppBoletaCampo) => {
    setCampoState(prev => fn(prev));
  }, []);

  // Cargar reporte existente al abrir
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from('app_boleta_campo')
        .select('*')
        .eq('boleta_id', boleta.id)
        .eq('empresa_id', empresaId)
        .maybeSingle();
      if (!alive) return;
      const inicial = data
        ? (data as AppBoletaCampo)
        : CAMPO_VACIO(empresaId, boleta.id, usuario);
      setCampoState(inicial);
      ultimaCampoSerialRef.current = JSON.stringify(inicial);
      setCargandoInicial(false);
    })();
    return () => { alive = false; };
  }, [boleta.id, empresaId, usuario]);

  // Auto-save con debounce 2s
  useEffect(() => {
    if (!campo.id) return;
    const serial = JSON.stringify(campo);
    if (serial === ultimaCampoSerialRef.current) return;
    setAutoSave('pendiente');
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(async () => {
      try {
        setAutoSave('guardando');
        const payload = {
          ...campo,
          registrado_por: campo.registrado_por || usuario,
          updated_at: new Date().toISOString(),
        };
        const { error } = await supabase
          .from('app_boleta_campo')
          .update(payload)
          .eq('id', campo.id!);
        if (error) throw error;
        ultimaCampoSerialRef.current = serial;
        setAutoSave('guardado');
      } catch (err: any) {

        setAutoSave('error');
      }
    }, 2000);
    return () => {
      if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    };
  }, [campo, usuario]);

  async function marcarInicio() {
    setCapturandoGps('inicio');
    try {
      const pos = await obtenerUbicacion();
      const base: AppBoletaCampo = {
        ...campo,
        inicio_at: pos.at,
        inicio_lat: pos.lat,
        inicio_lng: pos.lng,
        inicio_precision: pos.accuracy,
        registrado_por: usuario,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = base.id
        ? await supabase.from('app_boleta_campo').update(base).eq('id', base.id).select().single()
        : await supabase.from('app_boleta_campo').insert(base).select().single();
      if (error) throw error;
      setCampoState(data as AppBoletaCampo);
      showMsg('ok', `Inicio registrado: ${fmtCoord(pos.lat, pos.lng)} (±${pos.accuracy.toFixed(0)}m)`);
    } catch (e: any) {
      showMsg('err', e?.message || 'No se pudo capturar el GPS de inicio.');
    } finally {
      setCapturandoGps(null);
    }
  }

  async function marcarFin() {
    setCapturandoGps('fin');
    try {
      const pos = await obtenerUbicacion();
      const base: AppBoletaCampo = {
        ...campo,
        fin_at: pos.at,
        fin_lat: pos.lat,
        fin_lng: pos.lng,
        fin_precision: pos.accuracy,
        registrado_por: usuario,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = base.id
        ? await supabase.from('app_boleta_campo').update(base).eq('id', base.id).select().single()
        : await supabase.from('app_boleta_campo').insert(base).select().single();
      if (error) throw error;
      setCampoState(data as AppBoletaCampo);
      showMsg('ok', `Fin registrado: ${fmtCoord(pos.lat, pos.lng)} (±${pos.accuracy.toFixed(0)}m)`);
    } catch (e: any) {
      showMsg('err', e?.message || 'No se pudo capturar el GPS de fin.');
    } finally {
      setCapturandoGps(null);
    }
  }

  async function guardar() {
    if (!campo.fin_at) {
      showMsg('err', 'Marcá "Finalizar aplicación" cuando termines de aplicar para poder guardar.'); return;
    }
    if (!campo.boom_id) {
      showMsg('err', 'Seleccione el boom utilizado.'); return;
    }
    if (!campo.metodo_aspersion && !campo.metodo_inmersion && !campo.metodo_nebulizacion) {
      showMsg('err', 'Seleccione el método de aplicación.'); return;
    }

    setGuardando(true);
    const payload = { ...campo, registrado_por: usuario, updated_at: new Date().toISOString() };
    const { error } = campo.id
      ? await supabase.from('app_boleta_campo').update(payload).eq('id', campo.id)
      : await supabase.from('app_boleta_campo').insert(payload);
    setGuardando(false);
    if (error) { showMsg('err', error.message); return; }
    showMsg('ok', 'Reporte de campo guardado.');
    onGuardado?.();
    onClose();
  }

  if (cargandoInicial) return null;

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)',
      overflowY: 'auto', padding: '16px',
    }}>
      <div style={{
        maxWidth: '560px', margin: '0 auto', borderRadius: '18px', padding: '24px',
        background: '#0d1117', border: '1px solid var(--line)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc' }}>Reporte de Campo</div>
            <div style={{ fontSize: '13px', color: 'var(--ink-muted)' }}>
              Lote {boleta.lote_num} · {boleta.etapa_nombre} · Sem. {boleta.semana}
            </div>
          </div>
          <button onClick={onClose} style={{
            padding: '6px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
            border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
          }}>Cerrar</button>
        </div>

        {/* Mensaje rápido */}
        {msg && (
          <div style={{
            padding: '8px 12px', borderRadius: '8px', marginBottom: '12px',
            fontSize: '13px', fontWeight: 600,
            background: msg.tipo === 'ok' ? '#0f2c20' : '#34181c',
            border: `1px solid ${msg.tipo === 'ok' ? '#1d6e4f' : '#7d2f3a'}`,
            color: msg.tipo === 'ok' ? '#9df4c7' : '#ffb3bb',
          }}>{msg.texto}</div>
        )}

        {/* Indicador de auto-guardado */}
        {campo.id && (
          <div style={{
            fontSize: '11px', fontWeight: 600, padding: '6px 10px', borderRadius: '6px',
            marginBottom: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: autoSave === 'guardado'  ? 'rgba(34,197,94,0.08)'
                    : autoSave === 'guardando' ? 'rgba(59,130,246,0.10)'
                    : autoSave === 'pendiente' ? 'rgba(245,158,11,0.10)'
                    : autoSave === 'error'     ? 'rgba(239,68,68,0.10)'
                    : 'rgba(255,255,255,0.04)',
            color: autoSave === 'guardado'  ? '#4ade80'
                 : autoSave === 'guardando' ? '#93c5fd'
                 : autoSave === 'pendiente' ? '#fbbf24'
                 : autoSave === 'error'     ? '#fca5a5'
                 : 'var(--ink-faint)',
            border: '1px solid',
            borderColor: autoSave === 'guardado'  ? 'rgba(34,197,94,0.25)'
                      : autoSave === 'guardando' ? 'rgba(59,130,246,0.30)'
                      : autoSave === 'pendiente' ? 'rgba(245,158,11,0.30)'
                      : autoSave === 'error'     ? 'rgba(239,68,68,0.30)'
                      : 'transparent',
          }}>
            {autoSave === 'guardado'  ? '✓ Guardado automáticamente'
            : autoSave === 'guardando' ? '⏳ Guardando...'
            : autoSave === 'pendiente' ? '… cambios sin guardar'
            : autoSave === 'error'     ? '⚠ Error al guardar — revise conexión'
            : '✓ Sin cambios'}
          </div>
        )}

        {/* Inicio y fin de aplicacion con GPS */}
        <Seccion titulo="Inicio y fin de aplicación">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {/* INICIO */}
            <div style={{
              padding: '12px', borderRadius: '12px',
              border: `1.5px solid ${campo.inicio_at ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.12)'}`,
              background: campo.inicio_at ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.03)',
            }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: campo.inicio_at ? '#4ade80' : 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                Inicio
              </div>
              {campo.inicio_at ? (
                <div style={{ fontSize: '12px', color: 'var(--ink)', lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 600 }}>{fmtDateTime(campo.inicio_at)}</div>
                  <div style={{ fontSize: '11px', color: 'var(--ink-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                    {fmtCoord(campo.inicio_lat, campo.inicio_lng)}
                  </div>
                  {campo.inicio_precision != null && (
                    <div style={{ fontSize: '10px', color: 'var(--ink-faint)' }}>
                      ± {Number(campo.inicio_precision).toFixed(0)} m
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
                  Sin registrar
                </div>
              )}
              {campo.inicio_at ? (
                <div style={{
                  width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                  fontSize: '13px', fontWeight: 700,
                  background: 'rgba(34,197,94,0.20)', color: '#86efac',
                  textAlign: 'center', cursor: 'default',
                  border: '1px solid rgba(34,197,94,0.35)',
                }}>
                  ✓ Iniciado · {new Date(campo.inicio_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              ) : (
                <button
                  onClick={marcarInicio}
                  disabled={capturandoGps !== null || guardando}
                  style={{
                    width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                    fontSize: '13px', fontWeight: 700,
                    border: 'none', cursor: capturandoGps !== null ? 'wait' : 'pointer',
                    background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                    color: '#fff',
                    opacity: capturandoGps !== null ? 0.6 : 1,
                  }}>
                  {capturandoGps === 'inicio' ? 'Capturando GPS...' : '▶ Iniciar aplicación'}
                </button>
              )}
            </div>

            {/* FIN */}
            <div style={{
              padding: '12px', borderRadius: '12px',
              border: `1.5px solid ${campo.fin_at ? 'rgba(239,68,68,0.45)' : 'rgba(255,255,255,0.12)'}`,
              background: campo.fin_at ? 'rgba(239,68,68,0.06)' : 'rgba(255,255,255,0.03)',
            }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: campo.fin_at ? '#fca5a5' : 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                Fin
              </div>
              {campo.fin_at ? (
                <div style={{ fontSize: '12px', color: 'var(--ink)', lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 600 }}>{fmtDateTime(campo.fin_at)}</div>
                  <div style={{ fontSize: '11px', color: 'var(--ink-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                    {fmtCoord(campo.fin_lat, campo.fin_lng)}
                  </div>
                  {campo.fin_precision != null && (
                    <div style={{ fontSize: '10px', color: 'var(--ink-faint)' }}>
                      ± {Number(campo.fin_precision).toFixed(0)} m
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
                  Sin registrar
                </div>
              )}
              {campo.fin_at ? (
                <div style={{
                  width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                  fontSize: '13px', fontWeight: 700,
                  background: 'rgba(239,68,68,0.20)', color: '#fca5a5',
                  textAlign: 'center', cursor: 'default',
                  border: '1px solid rgba(239,68,68,0.35)',
                }}>
                  ✓ Finalizado · {new Date(campo.fin_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              ) : (
                <button
                  onClick={marcarFin}
                  disabled={capturandoGps !== null || guardando || !campo.inicio_at}
                  title={!campo.inicio_at ? 'Marque primero el inicio' : ''}
                  style={{
                    width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px',
                    fontSize: '13px', fontWeight: 700,
                    border: 'none', cursor: capturandoGps !== null || !campo.inicio_at ? 'not-allowed' : 'pointer',
                    background: !campo.inicio_at ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg,#dc2626,#ef4444)',
                    color: !campo.inicio_at ? 'rgba(255,255,255,0.3)' : '#fff',
                    opacity: capturandoGps !== null ? 0.6 : 1,
                  }}>
                  {capturandoGps === 'fin' ? 'Capturando GPS...' : '■ Finalizar aplicación'}
                </button>
              )}
            </div>
          </div>
          {campo.inicio_at && campo.fin_at && (() => {
            const ms = new Date(campo.fin_at).getTime() - new Date(campo.inicio_at).getTime();
            if (!Number.isFinite(ms) || ms <= 0) return null;
            const minutos = Math.round(ms / 60000);
            const horas = Math.floor(minutos / 60);
            const mins = minutos % 60;
            return (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#93c5fd', textAlign: 'center' }}>
                Duración: {horas > 0 ? `${horas} h ${mins} min` : `${mins} min`}
              </div>
            );
          })()}
        </Seccion>

        {/* Operario y equipo */}
        <Seccion titulo="Operario y equipo">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '4px' }}>OPERARIO</div>
              <div style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '15px',
                border: '1.5px solid var(--line)', background: 'rgba(255,255,255,0.03)',
                color: 'var(--ink)', fontWeight: 600,
              }}>
                {campo.boomero_nombre || usuario || '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '8px' }}>BOOM *</div>
              {booms.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#f59e0b', padding: '10px 12px', borderRadius: '8px', border: '1px dashed rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)' }}>
                  No hay booms configurados. Cargá los booms en Plan de Manejo.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                  {booms.map(b => {
                    const seleccionado = campo.boom_id === b.id;
                    return (
                      <button key={b.id} onClick={() => setCampo(p => ({
                        ...p,
                        boom_id: b.id,
                        boom_descripcion: `${b.nombre}${b.capacidad_l ? ` · ${b.capacidad_l} L` : ''}`,
                      }))} style={{
                        padding: '12px 10px', borderRadius: '10px',
                        cursor: 'pointer', textAlign: 'left',
                        border: `1.5px solid ${seleccionado ? '#3b82f6' : 'rgba(255,255,255,0.12)'}`,
                        background: seleccionado ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                        color: seleccionado ? '#93c5fd' : 'var(--ink)',
                        transition: 'all 0.15s',
                      }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '2px' }}>{b.nombre}</div>
                        <div style={{ fontSize: '11px', color: seleccionado ? '#93c5fd' : 'var(--ink-faint)' }}>
                          {b.capacidad_l ? `${b.capacidad_l} L` : ''}{b.descripcion ? ` · ${b.descripcion}` : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Seccion>

        {/* Calibración */}
        <Seccion titulo="Información de calibración">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <NumInput label="RPM" value={campo.rpm} placeholder="0" onChange={v => setCampo(p => ({ ...p, rpm: v }))} />
            <NumInput label="PRESIÓN (bar)" value={campo.presion_bar} placeholder="0.00" onChange={v => setCampo(p => ({ ...p, presion_bar: v }))} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: '8px' }}>MARCHA</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {MARCHAS.map(m => (
                <button key={m} onClick={() => setCampo(p => ({ ...p, marcha: m }))} style={{
                  padding: '10px 18px', borderRadius: '10px', fontSize: '15px', fontWeight: 700,
                  cursor: 'pointer',
                  border: `1.5px solid ${campo.marcha === m ? '#3b82f6' : 'rgba(255,255,255,0.12)'}`,
                  background: campo.marcha === m ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                  color: campo.marcha === m ? '#93c5fd' : 'var(--ink-muted)',
                }}>{m}</button>
              ))}
            </div>
          </div>
        </Seccion>

        {/* Método */}
        <Seccion titulo="Método de aplicación *">
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <CheckChip label="Aspersión"    checked={!!campo.metodo_aspersion}    onChange={v => setCampo(p => ({ ...p, metodo_aspersion: v }))} />
            <CheckChip label="Inmersión"    checked={!!campo.metodo_inmersion}    onChange={v => setCampo(p => ({ ...p, metodo_inmersion: v }))} />
            <CheckChip label="Nebulización" checked={!!campo.metodo_nebulizacion} onChange={v => setCampo(p => ({ ...p, metodo_nebulizacion: v }))} />
          </div>
        </Seccion>

        {/* Área */}
        <Seccion titulo="Aplicado en">
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <CheckChip label="Cultivo"          checked={!!campo.cultivo}       onChange={v => setCampo(p => ({ ...p, cultivo: v }))} />
            <CheckChip label="Semillero"        checked={!!campo.semillero}     onChange={v => setCampo(p => ({ ...p, semillero: v }))} />
            <CheckChip label="Área de barbecho" checked={!!campo.area_barbecho} onChange={v => setCampo(p => ({ ...p, area_barbecho: v }))} />
          </div>
        </Seccion>

        {/* Caldo sobrante */}
        <Seccion titulo="Caldo sobrante">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <CheckChip label="Sí" checked={campo.caldo_sobrante === true} onChange={() => setCampo(p => ({ ...p, caldo_sobrante: true }))} />
            <CheckChip label="No" checked={campo.caldo_sobrante === false} onChange={() => setCampo(p => ({ ...p, caldo_sobrante: false, caldo_sobrante_litros: undefined }))} />
            {campo.caldo_sobrante && (
              <div style={{ flex: 1, minWidth: '140px' }}>
                <NumInput label="LITROS SOBRANTES" value={campo.caldo_sobrante_litros} placeholder="0.00" onChange={v => setCampo(p => ({ ...p, caldo_sobrante_litros: v }))} />
              </div>
            )}
          </div>
        </Seccion>

        {/* Condiciones climáticas */}
        <Seccion titulo="Condiciones climáticas">
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <CheckChip label="Nublado"  checked={!!campo.clima_nublado}  onChange={v => setCampo(p => ({ ...p, clima_nublado: v }))} />
            <CheckChip label="Lluvioso" checked={!!campo.clima_lluvioso} onChange={v => setCampo(p => ({ ...p, clima_lluvioso: v }))} />
            <CheckChip label="Soleado"  checked={!!campo.clima_soleado}  onChange={v => setCampo(p => ({ ...p, clima_soleado: v }))} />
            <CheckChip label="Ventoso"  checked={!!campo.clima_ventoso}  onChange={v => setCampo(p => ({ ...p, clima_ventoso: v }))} />
            <CheckChip label="No Apto"  checked={!!campo.clima_no_apto}  onChange={v => setCampo(p => ({ ...p, clima_no_apto: v }))} />
          </div>
        </Seccion>

        {/* Observaciones */}
        <Seccion titulo="Observaciones">
          <textarea
            value={campo.observaciones ?? ''}
            onChange={e => setCampo(p => ({ ...p, observaciones: e.target.value }))}
            placeholder="Notas adicionales..."
            rows={3}
            style={{
              width: '100%', padding: '12px', borderRadius: '10px', fontSize: '15px',
              border: '1px solid var(--line)', background: 'var(--surface-base)',
              color: '#f8fafc', resize: 'none', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </Seccion>

        {/* Botón guardar */}
        {(() => {
          const sinFin = !campo.fin_at;
          return (
            <button
              onClick={guardar}
              disabled={guardando || sinFin}
              style={{
                width: '100%', padding: '18px', borderRadius: '14px', fontSize: '18px', fontWeight: 800,
                border: 'none', letterSpacing: '0.02em',
                background: guardando || sinFin
                  ? 'rgba(255,255,255,0.07)'
                  : 'linear-gradient(135deg,#f59e0b,#fbbf24)',
                color: guardando || sinFin ? 'rgba(255,255,255,0.4)' : '#0d1117',
                cursor: guardando || sinFin ? 'not-allowed' : 'pointer',
              }}>
              {guardando
                ? 'Guardando...'
                : sinFin
                  ? 'Pendiente: marcar Finalizar aplicación'
                  : '✓ Guardar reporte de campo'}
            </button>
          );
        })()}
      </div>
    </div>,
    document.body,
  );
}
